import { useCallback, useEffect, useState } from 'react';
import { apiFetch, obtenerBlobUrl } from '../../lib/api';
import { Ayuda } from '../../components/Ayuda';
import { Modal } from '../../components/Modal';
import { Spinner } from '../../components/Spinner';
import { RAIZ, type Favorito, type Item } from './tipos';

/** Explorador de SharePoint para AGREGAR una DIA (contenido del panel lateral, 2026-10-22): navegar,
 *  buscar, Ver/Abrir archivos y marcar una carpeta con ★ (trabajo o referencia). Al marcar, avisa al
 *  padre con el id del favorito nuevo para que abra el pre-análisis. */
export function ExploradorSharePoint({ favoritos, onMarcado }: { favoritos: Favorito[]; onMarcado: (favoritoId: string) => Promise<void> }) {
  const [ruta, setRuta] = useState(RAIZ);
  const [items, setItems] = useState<Item[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [marcando, setMarcando] = useState<(Item & { ruta: string }) | null>(null);
  const [nombreFav, setNombreFav] = useState('');
  const [tipoFav, setTipoFav] = useState<'trabajo' | 'referencia'>('trabajo');
  const [guardando, setGuardando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [resultados, setResultados] = useState<Item[] | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [viendo, setViendo] = useState<string | null>(null);

  const navegar = useCallback(async (r: string) => {
    setCargando(true);
    setError(null);
    setResultados(null);
    try {
      const d = await apiFetch<{ items: Item[] }>(`/api/sharepoint/navegar?ruta=${encodeURIComponent(r)}`);
      setItems(d.items);
      setRuta(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo leer el SharePoint.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { void navegar(RAIZ); }, [navegar]);

  async function guardarFavorito() {
    if (!marcando) return;
    setGuardando(true);
    try {
      const r = await apiFetch<{ favorito: Favorito }>('/api/sharepoint/favoritos', { method: 'POST', body: JSON.stringify({ nombre: nombreFav.trim(), ruta: marcando.ruta, tipo: tipoFav }) });
      setMarcando(null);
      setNombreFav('');
      // Estudio en fases: el padre abre el modal de PRE-ANÁLISIS (spinner → resumen → Estudiar).
      await onMarcado(r.favorito.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar el favorito.');
      setMarcando(null);
    } finally {
      setGuardando(false);
    }
  }

  async function buscar() {
    if (busqueda.trim().length < 2) return;
    setBuscando(true);
    setError(null);
    try {
      const d = await apiFetch<{ items: Item[] }>(`/api/sharepoint/buscar?q=${encodeURIComponent(busqueda.trim())}&ruta=${encodeURIComponent(RAIZ)}`);
      setResultados(d.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'La búsqueda falló.');
    } finally {
      setBuscando(false);
    }
  }

  /** "Ver": trae el binario por el proxy autenticado y lo abre en una pestaña. */
  async function verArchivo(it: Item) {
    const clave = it.id ?? it.ruta ?? it.nombre;
    setViendo(clave);
    try {
      const qs = it.id ? `id=${encodeURIComponent(it.id)}` : `ruta=${encodeURIComponent(it.ruta ?? '')}`;
      const url = await obtenerBlobUrl(`/api/sharepoint/archivo?${qs}`);
      window.open(url, '_blank', 'noopener');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo abrir el archivo.');
    } finally {
      setViendo(null);
    }
  }

  const migas = ruta.split('/');
  const rutasFavoritas = new Set(favoritos.map((f) => f.ruta));
  const lista = resultados ?? items;

  const FilaAcciones = ({ it }: { it: Item }) => (
    <span className="flex shrink-0 items-center gap-1">
      {it.tipo === 'archivo' && (
        <>
          <button type="button" onClick={() => void verArchivo(it)} disabled={viendo === (it.id ?? it.ruta ?? it.nombre)} className="rounded-lg px-2 py-1 text-[11.5px] font-medium text-azul-profundo hover:bg-celeste/15 disabled:opacity-50" title="Ver el archivo aquí mismo (proxy seguro)">
            {viendo === (it.id ?? it.ruta ?? it.nombre) ? '…' : 'Ver'}
          </button>
          {it.webUrl && (
            <a href={it.webUrl} target="_blank" rel="noopener noreferrer" className="rounded-lg px-2 py-1 text-[11.5px] font-medium text-gris hover:bg-celeste/15 hover:text-azul-profundo" title="Abrir en SharePoint">
              Abrir
            </a>
          )}
        </>
      )}
      {it.tipo === 'carpeta' && it.ruta !== null && (
        rutasFavoritas.has(it.ruta) ? (
          <span className="px-2 text-sm text-amber-500" title="Ya está en DIAs en trabajo">★</span>
        ) : (
          <button
            type="button"
            onClick={() => { setMarcando(it as Item & { ruta: string }); setNombreFav(it.nombre); }}
            className="rounded px-2 py-1 text-sm text-slate-300 transition-colors hover:text-amber-500"
            aria-label={`Marcar ${it.nombre} como DIA favorita`}
            title="Marcar como DIA en trabajo"
          >
            ★
          </button>
        )
      )}
    </span>
  );

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[12px] font-normal leading-relaxed text-gris">
        Navega o busca la carpeta del proyecto en SharePoint y márcala con la estrella ★. Andrés estudiará sus documentos y la DIA aparecerá en tu listado.
        <Ayuda texto="Marca la carpeta ESPECÍFICA del proyecto (no la carpeta del cliente completa): el estudio es más rápido y Andrés no mezcla proyectos. Si la carpeta es muy grande, el análisis previo te lo advierte." />
      </p>
      {error && <p role="alert" className="alerta-error">{error}</p>}
      
      <section>
        <div className="flex flex-wrap items-center justify-between gap-2">
          
          {(
          <form onSubmit={(e) => { e.preventDefault(); void buscar(); }} className="flex min-w-[220px] flex-1 gap-1.5 sm:max-w-xs">
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar archivos…"
              className="campo !h-8 flex-1 !text-[12.5px]"
              aria-label="Buscar archivos en SharePoint"
            />
            <button type="submit" disabled={buscando || busqueda.trim().length < 2} className="btn-secundario !h-8 !px-3 !text-[12px] disabled:opacity-50">
              {buscando ? '…' : 'Buscar'}
            </button>
          </form>
          )}
        </div>

        {(resultados !== null ? (
          <div className="mt-2 flex items-center gap-2 text-[11.5px] font-normal text-gris">
            <span>{resultados.length} resultado{resultados.length === 1 ? '' : 's'} para "{busqueda.trim()}"</span>
            <button type="button" onClick={() => setResultados(null)} className="font-medium text-azul-profundo hover:underline">Limpiar</button>
          </div>
        ) : (
          <nav aria-label="Ruta" className="mt-2 flex flex-wrap items-center gap-1 text-[11.5px] font-normal text-gris">
            {migas.map((m, i) => (
              <span key={i} className="flex items-center gap-1">
                {i > 0 && <span aria-hidden="true">/</span>}
                <button
                  type="button"
                  onClick={() => void navegar(migas.slice(0, i + 1).join('/'))}
                  className={`rounded px-1 py-0.5 hover:bg-celeste/20 ${i === migas.length - 1 ? 'font-medium text-azul-profundo' : ''}`}
                >
                  {m}
                </button>
              </span>
            ))}
          </nav>
        ))}

        {(cargando || buscando ? (
          <div className="flex justify-center py-8"><Spinner tamano="md" /></div>
        ) : (
          <ul className="mt-2 divide-y divide-slate-100">
            {lista.map((it) => (
              <li key={it.id ?? it.ruta ?? it.nombre} className="flex items-center gap-2 py-1">
                {it.tipo === 'carpeta' ? (
                  <button type="button" onClick={() => { if (it.ruta !== null) void navegar(it.ruta); }} className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2 py-1 text-left hover:bg-celeste/10">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-azul-medio" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
                    <span className="min-w-0">
                      <span className="block truncate text-[12.5px] font-normal text-texto">{it.nombre}</span>
                      {resultados !== null && <span className="block truncate text-[10.5px] font-light text-gris">{it.ruta}</span>}
                    </span>
                  </button>
                ) : (
                  <span className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-gris" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6" /></svg>
                    <span className="min-w-0">
                      <span className="block truncate text-[12.5px] font-light text-texto">{it.nombre}</span>
                      {resultados !== null && <span className="block truncate text-[10.5px] font-light text-gris">{it.ruta}</span>}
                    </span>
                    {it.size !== null && <span className="shrink-0 text-[10.5px] font-normal text-gris">{Math.max(1, Math.round(it.size / 1024))} KB</span>}
                  </span>
                )}
                <FilaAcciones it={it} />
              </li>
            ))}
            {lista.length === 0 && <li className="py-5 text-center text-sm font-light text-gris">{resultados !== null ? 'Sin resultados.' : 'Carpeta vacía.'}</li>}
          </ul>
        ))}
      </section>

      {/* Modal: nombre libre del favorito */}
      {marcando && (
        <Modal titulo="Marcar DIA en trabajo" onCerrar={() => setMarcando(null)} bloqueante>
          <p className="text-sm font-normal text-texto">Carpeta: <span className="font-medium">{marcando.ruta}</span></p>
          <label className="etiqueta mt-4" htmlFor="fav-nombre">Nombre del favorito (a tu criterio)</label>
          <input id="fav-nombre" value={nombreFav} onChange={(e) => setNombreFav(e.target.value)} className="campo mt-1" placeholder="p. ej. DIA Flux Solar — PFV Loncura" />
          <fieldset className="mt-4">
            <legend className="etiqueta">¿Cómo la usará Andrés?</legend>
            <div className="mt-1.5 flex flex-col gap-1.5">
              <label className={`flex cursor-pointer items-start gap-2 rounded-xl border px-3 py-2 ${tipoFav === 'trabajo' ? 'border-azul-profundo/50 bg-celeste/10' : 'border-slate-200'}`}>
                <input type="radio" name="tipo-fav" checked={tipoFav === 'trabajo'} onChange={() => setTipoFav('trabajo')} className="mt-0.5" />
                <span className="text-[13px] font-normal text-texto">★ <span className="font-medium">DIA en trabajo</span> — proyecto activo: chat propio y seguimiento.</span>
              </label>
              <label className={`flex cursor-pointer items-start gap-2 rounded-xl border px-3 py-2 ${tipoFav === 'referencia' ? 'border-azul-profundo/50 bg-celeste/10' : 'border-slate-200'}`}>
                <input type="radio" name="tipo-fav" checked={tipoFav === 'referencia'} onChange={() => setTipoFav('referencia')} className="mt-0.5" />
                <span className="text-[13px] font-normal text-texto">📚 <span className="font-medium">Caso de referencia</span> — DIA terminada/ingresada al SEIA: entrena a Andrés y la usa como ejemplar en TODOS los chats.</span>
              </label>
            </div>
          </fieldset>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" onClick={() => setMarcando(null)} disabled={guardando} className="btn-fantasma">Cancelar</button>
            <button type="button" onClick={() => void guardarFavorito()} disabled={guardando || !nombreFav.trim()} className="btn-primario disabled:opacity-50">
              {guardando ? 'Guardando…' : tipoFav === 'referencia' ? 'Marcar como caso de referencia' : 'Marcar como DIA en trabajo'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
